/**
 * audio-delay.js — эхо с обратной связью (window.AudioDelay)
 *
 * ОДНА реализация на живой тракт и на офлайн-рендер. Причина ровно та же, по
 * которой рядом лежит audio-saturation.js: в /audio-mixer граф дорожки написан
 * ДВАЖДЫ — buildTrackChain() для прослушки и заново внутри экспорта для
 * OfflineAudioContext. Эффект, собранный там двумя копиями кода, расходится не
 * сразу и не громко: человек слышит одно, а скачивает другое, и узнаёт об этом
 * только из готового файла. Модуль без зависимостей специально — его можно
 * позвать из любого контекста, включая офлайновый.
 *
 * Топология — посыл с обратной связью, СУХОГО сигнала внутри нет:
 *
 *     input ──► delay ──┬──► output   (только хвост)
 *                 ▲     │
 *                 └─ fb ┘
 *
 * Смешивает потребитель — в микшере хвост приходит в тот же merger, куда
 * приходит хвост реверба. Так у вызывающего остаётся ОДИН узел громкости на
 * посыл и не появляется второй сухой путь, который пришлось бы держать в фазе
 * с первым.
 *
 * ⚠️ `maxDelayTime` у DelayNode ФИКСИРУЕТСЯ при создании и потом не растёт:
 * узел, созданный под 0.3 с, молча зажмёт любое большее значение — ручка
 * поедет, а звук нет. Поэтому буфер здесь всегда MAX_TIME_S, а ручка двигает
 * только `delayTime`.
 *
 * ⚠️ Обратная связь ≥ 1 — самовозбуждение: хвост растёт без предела, пока не
 * упрётся в потолок и не превратится в грязь (а в офлайн-рендере это ещё и
 * никем не слышно до скачивания). Потолок MAX_FEEDBACK снимается только вместе
 * с этим абзацем.
 */
(function () {
'use strict';

var MAX_TIME_S = 2.0;      // потолок буфера: 2 с покрывает всё, что имеет смысл
var MAX_FEEDBACK = 0.9;    // выше — самовозбуждение
var LOOP_Q_DB = -3.0103;   // 20·log10(1/√2): Баттерворт в единицах Web Audio (дБ), см. build()
var RAMP_S = 0.02;         // ручка времени без щелчка

function clampTime(ms) {
    var s = (typeof ms === 'number' && isFinite(ms)) ? ms / 1000 : 0.3;
    if (s < 0) s = 0;
    return s > MAX_TIME_S ? MAX_TIME_S : s;
}

function clampFeedback(pct) {
    var v = (typeof pct === 'number' && isFinite(pct)) ? pct / 100 : 0.3;
    if (v < 0) v = 0;
    return v > MAX_FEEDBACK ? MAX_FEEDBACK : v;
}

/**
 * @param {BaseAudioContext} ctx  живой или OfflineAudioContext — безразлично
 * @param {{timeMs:number, feedback:number}} opts
 * @returns {{input:GainNode, output:GainNode, setTime:Function, setFeedback:Function}}
 */
function build(ctx, opts) {
    opts = opts || {};
    var input = ctx.createGain();
    var output = ctx.createGain();
    var delay = ctx.createDelay(MAX_TIME_S);
    var fb = ctx.createGain();
    // Срез верха в петле: без него каждое повторение звучит так же ярко, как
    // оригинал, и хвост читается как дребезг, а не как эхо.
    // ⚠️ Q у lowpass/highpass Web Audio читает в ДЕЦИБЕЛАХ: «0.7» — это не
    // «чуть мягче Баттерворта», а резонанс +1.74 дБ у среза. Фильтр стоит В
    // ПЕТЛЕ, и усиление петли на этой частоте было 0.9 × 1.22 = 1.10: при
    // обратной связи выше 82 % хвост не гас, а рос (замер 07.10.2026 на этом
    // модуле: 90 %, 300 мс — +2.7 дБ/с, через 20 с выше полной шкалы; и в
    // прослушке, и в экспорте). LOOP_Q_DB — Баттерворт: усиление фильтра нигде
    // не выше единицы, и потолок MAX_FEEDBACK снова значит то, что написано выше.
    var tone = ctx.createBiquadFilter();
    tone.type = 'lowpass';
    tone.frequency.value = 6000;
    tone.Q.value = LOOP_Q_DB;

    delay.delayTime.value = clampTime(opts.timeMs);
    fb.gain.value = clampFeedback(opts.feedback);

    input.connect(delay);
    delay.connect(tone);
    tone.connect(fb);
    fb.connect(delay);
    delay.connect(output);

    return {
        input: input,
        output: output,
        delay: delay,
        setTime: function (ms) {
            var v = clampTime(ms);
            try { delay.delayTime.setTargetAtTime(v, ctx.currentTime, RAMP_S); }
            catch (_) { delay.delayTime.value = v; }
        },
        setFeedback: function (pct) {
            var v = clampFeedback(pct);
            try { fb.gain.setTargetAtTime(v, ctx.currentTime, RAMP_S); }
            catch (_) { fb.gain.value = v; }
        }
    };
}

window.AudioDelay = { build: build, MAX_TIME_S: MAX_TIME_S, MAX_FEEDBACK: MAX_FEEDBACK, LOOP_Q_DB: LOOP_Q_DB };
})();
